import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { sendPasswordReset } from "@/modules/core/data/authRepository";
import {
  validateInvitation,
  checkEmailVerified,
  saveUserConsent,
  sendWelcomeEmail,
  acceptPendingInvitation,
} from "@/modules/core/data/authRepository";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { RabbitLoader } from "@/components/ui/rabbit-loader";
import { Loader2, AlertCircle, Mail } from "lucide-react";
import logoLapyno from "@/assets/logo-lapyno.png";
import { z } from "zod";
import { toast } from "sonner";
import { AuthLayout, AuthInput, AuthButton, AuthDivider, EmailVerification, PasswordStrengthIndicator } from "@/components/auth";
import { WorkspaceSetupLoader } from "@/components/auth/WorkspaceSetupLoader";

const emailSchema = z.string().email("Veuillez entrer une adresse email valide");
const passwordSchema = z
  .string()
  .min(8, "Le mot de passe doit contenir au moins 8 caractères")
  .regex(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule")
  .regex(/[a-z]/, "Le mot de passe doit contenir au moins une minuscule")
  .regex(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre")
  .regex(/[^A-Za-z0-9]/, "Le mot de passe doit contenir au moins un caractère spécial");

interface InvitationInfo {
  email: string;
  org_name: string;
  role: string;
  inviter_name: string;
}

interface AuthProps {
  initialMode?: "login" | "signup";
}

export default function Auth({ initialMode }: AuthProps = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get("invitation");

  const { user, profile, loading: authLoading, signIn, signUp, signOut } = useAuth();
  const [hasRedirected, setHasRedirected] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(searchParams.get("error"));
  const [success, setSuccess] = useState<string | null>(null);

  // Invitation state
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(false);
  const [acceptingInvitation, setAcceptingInvitation] = useState(false);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">(initialMode === "signup" ? "signup" : "signin");
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Email verification state - justSignedUp prevents redirect race condition
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);
  const [justSignedUp, setJustSignedUp] = useState(false);
  
  // Workspace preparation loader state (shown after email verification)
  const [showWorkspaceLoader, setShowWorkspaceLoader] = useState(false);

  // Store invitation token in localStorage immediately so it survives page reloads
  useEffect(() => {
    if (invitationToken) {
      localStorage.setItem('lapyno_pending_invitation', invitationToken);
    }
  }, [invitationToken]);

  // Load invitation info if token present
  useEffect(() => {
    const loadInvitationInfo = async () => {
      if (!invitationToken) return;

      setLoadingInvitation(true);
      try {
        const result = await validateInvitation(invitationToken);

        if (result.error) {
          setError(result.error);
          // Still set invitation info for graceful degradation
        }

        setInvitationInfo({
          email: result.data.email,
          org_name: result.data.org_name,
          role: result.data.role,
          inviter_name: result.data.inviter_name,
        });
        if (result.data.email) setEmail(result.data.email);
        setMode("signup");
      } catch (err) {
        console.error("Error loading invitation:", err);
        // Even on total failure, allow signup with the token
        setInvitationInfo({
          email: "",
          org_name: "votre workspace",
          role: "member",
          inviter_name: "Un administrateur",
        });
        setMode("signup");
      } finally {
        setLoadingInvitation(false);
      }
    };

    loadInvitationInfo();
  }, [invitationToken]);

  // Accept invitation after login
  const acceptInvitation = async () => {
    const tokenToUse = invitationToken || localStorage.getItem('lapyno_pending_invitation');
    if (!tokenToUse || !user) return;

    setAcceptingInvitation(true);
    try {
      const response = await acceptPendingInvitation(tokenToUse);

      // Clear pending token on success
      localStorage.removeItem('lapyno_pending_invitation');
      
      const orgId = response.organization?.id || "";
      const orgName = encodeURIComponent(response.organization?.name || "Workspace");
      const role = invitationInfo?.role || "member";

      window.location.href = `/invitation-welcome?orgId=${orgId}&orgName=${orgName}&role=${role}`;
    } catch (err: any) {
      console.error("Error accepting invitation:", err);
      toast.error(err.message || "Erreur lors de l'acceptation de l'invitation");
      // Clean up token to prevent flash on future logins
      localStorage.removeItem('lapyno_pending_invitation');
      navigate("/app", { replace: true });
    } finally {
      setAcceptingInvitation(false);
    }
  };

  // Check if user needs email verification
  useEffect(() => {
    const checkVerification = async () => {
      if (!user || !profile) return;

      const verified = await checkEmailVerified(user.id);
      if (!verified) {
        setPendingVerificationEmail(user.email || "");
        setShowEmailVerification(true);
      }
    };

    checkVerification();
  }, [user, profile]);

  useEffect(() => {
    const handleRedirect = async () => {
      if (hasRedirected) return;
      if (authLoading) return;
      if (!user) return;
      if (!profile?.email) return;
       
       // If email verification is already showing, don't redirect
       if (showEmailVerification) return;
       
       // If user just signed up, wait for email verification to show
       if (justSignedUp) return;

      // Check if email verification is needed
      const verified = await checkEmailVerified(user.id);
      if (!verified) {
        setPendingVerificationEmail(user.email || "");
        setShowEmailVerification(true);
        return;
      }

    const pendingToken = invitationToken || localStorage.getItem('lapyno_pending_invitation');
    if (pendingToken && !acceptingInvitation) {
        setHasRedirected(true);
        await acceptInvitation();
        return;
      }

      setHasRedirected(true);
      navigate("/app", { replace: true });
    };

    handleRedirect();
   }, [user, profile, authLoading, navigate, hasRedirected, invitationToken, acceptingInvitation, showEmailVerification, justSignedUp]);

  const validateForm = () => {
    try {
      emailSchema.parse(email);
      if (mode === "signup") {
        passwordSchema.parse(password);
      } else if (mode === "signin" && !password) {
        throw new z.ZodError([{ code: 'too_small', minimum: 1, type: 'string', inclusive: true, exact: false, message: 'Le mot de passe est requis', path: [] }]);
      }
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.errors[0].message);
      }
      return false;
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!validateForm()) return;

    setIsLoading(true);
    const { error } = await signIn(email, password);

    if (error) {
      if (error.message.includes("Invalid login credentials")) {
        setError("Identifiants incorrects. Vérifiez votre e-mail et mot de passe.");
      } else {
        setError(error.message);
      }
    }
    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!termsConsent) {
      setError("Vous devez accepter les conditions d'utilisation pour continuer.");
      return;
    }

    if (!validateForm()) return;

    // Store consent in localStorage to be saved after email confirmation
    localStorage.setItem(
      "pending_consent",
      JSON.stringify({
        marketing_consent: marketingConsent,
        terms_consent: termsConsent,
        consent_user_agent: navigator.userAgent,
      }),
    );

    setIsLoading(true);
    const fullName = `${firstName} ${lastName}`.trim();
    const result = await signUp(email, password, fullName);

    if (result.error) {
      localStorage.removeItem("pending_consent");
      if (result.error.message.includes("User already registered")) {
        setError("Un compte avec cet email existe déjà. Veuillez vous connecter.");
      } else {
        setError(result.error.message);
      }
      setIsLoading(false);
    } else {
      // After signup, mark as just signed up to prevent redirect race condition
      setJustSignedUp(true);
      setPendingVerificationEmail(email);
      setShowEmailVerification(true);
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      emailSchema.parse(email);
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.errors[0].message);
      }
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await sendPasswordReset(email);
      if (error) {
        setError(error);
      } else {
        setResetEmailSent(true);
        setSuccess("Un email de réinitialisation a été envoyé. Vérifiez votre boîte mail.");
      }
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'envoi");
    } finally {
      setIsLoading(false);
    }
  };


  const handleEmailVerified = async () => {
    setShowEmailVerification(false);
    
    // Show workspace preparation loader for 4 seconds
    setShowWorkspaceLoader(true);
    
    // Save pending consent if exists
    try {
      const pendingConsent = localStorage.getItem("pending_consent");
      if (pendingConsent && user) {
        const consent = JSON.parse(pendingConsent);
        await saveUserConsent(user.id, consent);
        localStorage.removeItem("pending_consent");
      }
    } catch (consentError) {
      console.error('Failed to save user consent:', consentError);
    }

    // Send welcome email
    if (user) {
      try {
        await sendWelcomeEmail(user.email!, user.user_metadata?.full_name);
      } catch (emailError) {
        console.error('Failed to send welcome email:', emailError);
      }
    }

    // Wait 4 seconds to show the workspace preparation loader
    await new Promise(resolve => setTimeout(resolve, 4000));

    // Redirect - check both URL and localStorage for invitation token
    const pendingToken = invitationToken || localStorage.getItem('lapyno_pending_invitation');
    if (pendingToken) {
      await acceptInvitation();
    } else {
      navigate("/app", { replace: true });
    }
  };

  const handleLogoutFromVerification = async () => {
    await signOut();
    setShowEmailVerification(false);
    setPendingVerificationEmail(null);
    setJustSignedUp(false);
    setMode("signin");
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      owner: "Propriétaire",
      admin: "Administrateur",
      member: "Membre",
      guest: "Invité",
    };
    return labels[role] || role;
  };

  // Show workspace preparation loader after email verification (4 seconds)
  if (showWorkspaceLoader) {
    return <WorkspaceSetupLoader />;
  }

  // Show email verification screen (don't require user - it may still be loading after signup)
  if (showEmailVerification && pendingVerificationEmail) {
    return (
      <EmailVerification
        email={pendingVerificationEmail}
        onVerified={handleEmailVerified}
        onLogout={handleLogoutFromVerification}
      />
    );
  }

  // Loading states
  if (authLoading || loadingInvitation || acceptingInvitation || (user && !profile)) {
    return (
      <AuthLayout>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          {acceptingInvitation && <p className="text-muted-foreground">Acceptation de l'invitation en cours...</p>}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm space-y-5">
        {/* Logo */}
        <div className="flex flex-col items-center">
          <a href="https://lapyno.com" target="_blank" rel="noopener noreferrer">
            <img src={logoLapyno} alt="Lapyno" className="h-16 w-auto object-contain mb-4 hover:opacity-80 transition-opacity cursor-pointer" />
          </a>

          {/* Header text based on mode */}
          {invitationInfo && mode !== "forgot" ? (
            <div className="text-center">
              <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
                <Mail className="h-5 w-5 text-violet-600" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">Invitation à rejoindre</h1>
              <p className="text-muted-foreground mt-2">
                <span className="font-semibold text-foreground">{invitationInfo.inviter_name}</span> vous invite à
                rejoindre <span className="font-semibold text-foreground">{invitationInfo.org_name}</span> en tant que{" "}
                <span className="font-semibold text-violet-600">{getRoleLabel(invitationInfo.role)}</span>
              </p>
              <p className="text-sm text-muted-foreground mt-3">
                {mode === "signup" ? (
                  <>
                    Vous avez déjà un compte ?{" "}
                    <button
                      type="button"
                      onClick={() => { setMode("signin"); setError(null); setSuccess(null); }}
                      className="text-violet-600 hover:text-violet-700 font-medium hover:underline"
                    >
                      Se connecter
                    </button>
                  </>
                ) : (
                  <>
                    Pas encore de compte ?{" "}
                    <button
                      type="button"
                      onClick={() => { setMode("signup"); setError(null); setSuccess(null); }}
                      className="text-violet-600 hover:text-violet-700 font-medium hover:underline"
                    >
                      Créer un compte
                    </button>
                  </>
                )}
              </p>
            </div>
          ) : mode === "forgot" ? (
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground">Mot de passe oublié ?</h1>
              <p className="text-muted-foreground mt-1">Entrez votre email pour recevoir un lien de réinitialisation</p>
            </div>
          ) : mode === "signup" ? (
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground">Inscription en moins d'une minute</h1>
              <p className="text-muted-foreground mt-1">
                Vous avez déjà un compte ?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("signin");
                    setError(null);
                    setSuccess(null);
                  }}
                  className="text-violet-600 hover:text-violet-700 font-medium hover:underline"
                >
                  Se connecter
                </button>
              </p>
            </div>
          ) : (
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground">Bienvenue !</h1>
              <p className="text-muted-foreground mt-1">
                Vous n'avez pas de compte ?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("signup");
                    setError(null);
                    setSuccess(null);
                  }}
                  className="text-violet-600 hover:text-violet-700 font-medium hover:underline"
                >
                  S'inscrire
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Error / Success messages */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Forms */}
        {mode === "forgot" ? (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <AuthInput
              type="email"
              placeholder="vous@entreprise.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <AuthButton type="submit" loading={isLoading} disabled={resetEmailSent}>
              {resetEmailSent ? "Email envoyé ✓" : "Envoyer le lien de réinitialisation"}
            </AuthButton>
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                setResetEmailSent(false);
                setError(null);
                setSuccess(null);
              }}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Retour à la connexion
            </button>
          </form>
        ) : mode === "signup" ? (
          <form onSubmit={handleSignUp} className="space-y-4">
            <button
              type="button"
              onClick={async () => {
                setIsLoading(true);
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: "google",
                  options: {
                    redirectTo: `${window.location.origin}/auth`,
                  },
                });
                if (error) {
                  toast.error(error.message || "Erreur lors de la connexion Google");
                  setIsLoading(false);
                }
              }}
              className="w-full flex items-center justify-center gap-3 h-11 rounded-md border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-sm font-medium text-gray-700">Continuer avec Google</span>
            </button>

            <AuthDivider />

            <div className="grid grid-cols-2 gap-3">
              <AuthInput
                type="text"
                placeholder="Prénom"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
              <AuthInput
                type="text"
                placeholder="Nom"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
            <AuthInput
              type="email"
              placeholder="E-mail professionnel"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <AuthInput
              type="password"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            <PasswordStrengthIndicator password={password} />

            {/* Consent checkboxes */}
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="marketing-consent"
                  checked={marketingConsent}
                  onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                  className="mt-0.5"
                />
                <label
                  htmlFor="marketing-consent"
                  className="text-xs text-muted-foreground leading-tight cursor-pointer"
                >
                  En cochant cette case, vous acceptez de recevoir des e-mails marketing de la part de Lapyno
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms-consent"
                  checked={termsConsent}
                  onCheckedChange={(checked) => setTermsConsent(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="terms-consent" className="text-xs text-muted-foreground leading-tight cursor-pointer">
                  En cochant cette case, vous acceptez nos{" "}
                  <a href="#" className="underline hover:text-foreground">
                    conditions d'utilisation
                  </a>{" "}
                  et notre{" "}
                  <a href="#" className="underline hover:text-foreground">
                    politique de confidentialité
                  </a>
                  , et consentez au transfert, à l'hébergement et au traitement des données hors UE.
                </label>
              </div>
            </div>

            <AuthButton type="submit" loading={isLoading} disabled={!termsConsent}>
              Créer un compte
            </AuthButton>
          </form>
        ) : (
          <form onSubmit={handleSignIn} className="space-y-4">
            <button
              type="button"
              onClick={async () => {
                setIsLoading(true);
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: "google",
                  options: {
                    redirectTo: `${window.location.origin}/auth`,
                  },
                });
                if (error) {
                  toast.error(error.message || "Erreur lors de la connexion Google");
                  setIsLoading(false);
                }
              }}
              className="w-full flex items-center justify-center gap-3 h-11 rounded-md border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-sm font-medium text-gray-700">Continuer avec Google</span>
            </button>

            <AuthDivider />

            <AuthInput
              type="email"
              placeholder="E-mail professionnel"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="auth-email-input"
            />
            <AuthInput
              type="password"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="auth-password-input"
            />
            <AuthButton
              type="submit"
              loading={isLoading}
              disabled={!email || !password}
              className={!email || !password ? "opacity-40" : ""}
              data-testid="auth-submit-signin"
            >
              Se connecter
            </AuthButton>
            <button
              type="button"
              onClick={() => {
                setMode("forgot");
                setError(null);
                setSuccess(null);
              }}
              className="w-full text-center text-sm text-violet-600 hover:text-violet-700 hover:underline transition-colors"
            >
              Mot de passe oublié ?
            </button>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
